import React from 'react';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@apollo/client';
import FieldLabel from '../../../../components/Form/FieldLabel';
import InputField from '../../../../components/Form/InputField';
import { VerticalField } from '../../../../components/Form';
import { useUserSettingsSelector } from '../../../../redux/selectorHooks';
import { useAppDispatch } from '../../../../redux/hooks';
import {
  IUserSettingUpdateMutation,
  IUserSettingUpdateMutationVariables,
  UserSettingKind,
} from '../../../../utils/apollo/graphqlSchema';
import { USER_SETTING_UPDATE } from '../../../../utils/apollo/userSettings';
import { IJupyterIntegrationState, jupyterIntegrationSchema } from './types';
import { PrimaryButton, SecondaryButton } from '../../../../components/Button';
import { setJupyterHost } from '../../../../redux/slices/userSettings';
import toastMessage from '../../../../utils/toastMessage';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';

const ActionsWrapper = styled.div`
  display: flex;
  column-gap: 16px;
`;

const JupyterIntegrationForm = () => {
  const dispatch = useAppDispatch();
  const currJupyterHost = useUserSettingsSelector('jupyterHost');

  const {
    handleSubmit,
    control,
    formState: { isSubmitting, isDirty },
    reset,
  } = useForm<IJupyterIntegrationState>({
    resolver: zodResolver(jupyterIntegrationSchema),
    defaultValues: {
      host: currJupyterHost,
    },
  });

  const [saveSettings] = useMutation<
    IUserSettingUpdateMutation,
    IUserSettingUpdateMutationVariables
  >(USER_SETTING_UPDATE);

  const submitForm = handleSubmit(async (formData) => {
    const response = await saveSettings({
      variables: {
        input: {
          userSettings: [
            {
              kind: UserSettingKind.JupyterHost,
              value: formData.host,
            },
          ],
        },
      },
    });

    if (printGraphqlErrors(response?.data?.userSettingUpdate?.errors)) {
      return;
    }

    dispatch(setJupyterHost(formData.host));
    toastMessage({
      message: `New Jupyter Host has been saved!`,
      type: 'info',
    });

    reset(formData);
  });

  const handleReset = () => {
    reset({ host: currJupyterHost });
  };

  return (
    <form onSubmit={submitForm}>
      <VerticalField>
        <FieldLabel>{'Jupyter Host'}</FieldLabel>
        <Controller
          name="host"
          control={control}
          render={({ field, fieldState }) => (
            <InputField
              {...field}
              placeholder="Jupyter Host"
              hasError={Boolean(fieldState.error)}
            />
          )}
        />
      </VerticalField>
      <ActionsWrapper>
        <PrimaryButton
          type="submit"
          disabled={isSubmitting || !isDirty}
          trackContext="settings"
          trackAction="jupyter - save"
        >
          Save
        </PrimaryButton>
        <SecondaryButton
          type="button"
          onClick={handleReset}
          disabled={isSubmitting || !isDirty}
          isOutlined={true}
          trackContext="settings"
          trackAction="jupyter - reset"
        >
          Reset
        </SecondaryButton>
      </ActionsWrapper>
    </form>
  );
};

export default JupyterIntegrationForm;
